import { Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { Segmented } from '../../components/Segmented';
import { SkeletonBlock } from '../../components/Skeleton';
import type { Appointment } from './api';
import {
  addDays,
  addMonths,
  daysOfView,
  rangeOf,
  startOfMonth,
  today,
  touchesDay,
  type CalendarView,
} from './dates';
import {
  formatDayHeading,
  formatDayMonth,
  formatDayNumber,
  formatPeriod,
  formatTimes,
} from './presentation';
import { useAppointments } from './queries';

/** Wie viele Termine eine Monatszelle zeigt, bevor sie "+n weitere" sagt. */
const MONTH_CELL_LIMIT = 3;

/**
 * Die eigene Terminuebersicht -- F1-54.
 *
 * **Woche und Monat, beide als Raster von Tagen.** Die Woche zeigt jeden
 * Termin mit Uhrzeit, Ticket und Ort; der Monat die ersten drei je Tag und
 * die Zahl der uebrigen. Eine Stundenleiste gibt es nicht: Ein Ticket-Termin
 * ist ein Einsatz und keine Besprechungsserie, und bei vier Terminen in der
 * Woche waere sie zu neun Zehnteln leer.
 *
 * **Ansicht und Tag stehen in der Adresse** (`?view=month&date=2026-09-01`).
 * So fuehrt "Zurueck" im Browser in die vorige Woche und nicht aus dem
 * Kalender heraus, und eine Woche laesst sich einem Kollegen schicken.
 *
 * **Nur die eigenen Termine** -- die der Kollegen waeren Einsatzplanung
 * (docs/06, "Bewusst nicht gebaut").
 */
export function CalendarPage({ view, date }: { view: CalendarView; date?: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const anchor = date ?? today();
  const days = daysOfView(view, anchor);
  const first = days[0] ?? anchor;
  const last = days[days.length - 1] ?? anchor;
  const query = useAppointments({ mine: true, ...rangeOf(first, last) });
  const list = query.data ?? [];
  const month = view === 'month';
  const currentMonth = anchor.slice(0, 7);
  const heute = today();

  const go = (next: { view?: CalendarView; date?: string }) => {
    void navigate({
      to: '/app/calendar',
      search: { view: next.view ?? view, date: next.date ?? anchor },
    });
  };

  const step = (direction: 1 | -1) => {
    go({ date: month ? addMonths(anchor, direction) : addDays(anchor, 7 * direction) });
  };

  return (
    <>
      <PageHeader
        title={t('appointments.calendarTitle')}
        subtitle={t('appointments.calendarSubtitle')}
      />

      <div className="calendar-toolbar">
        <Segmented<CalendarView>
          ariaLabel={t('appointments.view')}
          value={view}
          options={[
            { value: 'week', label: t('appointments.week') },
            { value: 'month', label: t('appointments.month') },
          ]}
          onChange={(value) => {
            go({ view: value });
          }}
        />
        <Button
          variant="secondary"
          onClick={() => {
            step(-1);
          }}
        >
          {t('appointments.previous')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            go({ date: heute });
          }}
        >
          {t('appointments.goToday')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            step(1);
          }}
        >
          {t('appointments.next')}
        </Button>
        <h2 className="calendar-toolbar__period" aria-live="polite">
          {formatPeriod(month ? startOfMonth(anchor) : first, last, month, i18n.language)}
        </h2>
      </div>

      {query.isError ? <ErrorNotice error={query.error} /> : null}
      {query.isPending ? <SkeletonBlock height="24rem" /> : null}

      {query.isSuccess ? (
        <>
          <div className={`calendar ${month ? 'calendar--month' : 'calendar--week'}`}>
            {days.slice(0, 7).map((day) => (
              <div className="calendar__weekday" aria-hidden="true" key={`head-${day}`}>
                {new Intl.DateTimeFormat(i18n.language, {
                  weekday: 'short',
                  timeZone: 'UTC',
                }).format(new Date(`${day}T12:00:00Z`))}
              </div>
            ))}
            {days.map((day) => {
              const entries = list.filter((item) => touchesDay(item.starts_at, item.ends_at, day));
              const shown = month ? entries.slice(0, MONTH_CELL_LIMIT) : entries;
              const classes = [
                'calendar__day',
                day === heute ? 'calendar__day--today' : '',
                month && day.slice(0, 7) !== currentMonth ? 'calendar__day--outside' : '',
              ]
                .filter(Boolean)
                .join(' ');

              return (
                <div
                  className={classes}
                  key={day}
                  aria-current={day === heute ? 'date' : undefined}
                >
                  {/* Sichtbar nur Zahl oder Datum; vorgelesen wird der ganze
                      Tag, denn die Spaltenkoepfe darueber sind fuer eine
                      Sprachausgabe nicht zugeordnet. */}
                  <span
                    className="calendar__date"
                    aria-label={formatDayHeading(day, i18n.language)}
                  >
                    {month ? formatDayNumber(day) : formatDayMonth(day, i18n.language)}
                  </span>
                  {shown.map((item) => (
                    <CalendarEntry key={item.id} item={item} detailed={!month} />
                  ))}
                  {entries.length > shown.length ? (
                    <button
                      type="button"
                      className="calendar__more"
                      onClick={() => {
                        go({ view: 'week', date: day });
                      }}
                    >
                      {t('appointments.more', { count: entries.length - shown.length })}
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>

          {list.length === 0 ? (
            <EmptyState
              icon="calendar"
              title={t('appointments.periodEmpty')}
              hint={t('appointments.periodEmptyHint')}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
}

function CalendarEntry({ item, detailed }: { item: Appointment; detailed: boolean }) {
  const { i18n } = useTranslation();
  const times = formatTimes(item.starts_at, item.ends_at, i18n.language);

  return (
    <Link
      to="/app/tickets/$ticketId"
      params={{ ticketId: item.ticket_id }}
      className="calendar__entry"
      title={[times, `${item.ticket_number} ${item.ticket_title}`, item.location]
        .filter(Boolean)
        .join(' · ')}
    >
      <span className="calendar__entry-time">{times}</span>
      {detailed ? (
        <>
          <br />
          {item.ticket_number} · {item.ticket_title}
          {item.customer_name ? (
            <>
              <br />
              {item.customer_name}
            </>
          ) : null}
          {item.location ? (
            <>
              <br />
              {item.location}
            </>
          ) : null}
        </>
      ) : (
        item.ticket_title
      )}
    </Link>
  );
}
